'use client';
import classNames from 'classnames';
import { useState } from 'react';
import { colors } from 'tailwind.config';

import { useStore } from '@/helpers/store';

type Props = {
  onTop?: boolean;
  show: boolean;
};

type DebugSection = {
  key: keyof ReturnType<typeof useStore.getState>;
  label: string;
};

const DEBUG_SECTIONS: DebugSection[] = [
  { key: 'activeSection', label: 'Active Section' },
  { key: 'inView', label: 'In View' },
  { key: 'marks', label: 'Marks' },
  { key: 'progressBySection', label: 'Progress' },
  { key: 'logoColor', label: 'Logo Color' },
  { key: 'backgroundColor', label: 'Background Color' },
  { key: 'defaults', label: 'Defaults' },
];

export const Debug = (props: Props) => {
  const { onTop = false, show = true } = props;
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  // Get all store values
  const storeState = useStore((state) => state);
  const textColor = colors.at;

  const toggleSection = (sectionKey: string): void => {
    const newVisibleSections = new Set(visibleSections);
    if (newVisibleSections.has(sectionKey)) {
      newVisibleSections.delete(sectionKey);
    } else {
      newVisibleSections.add(sectionKey);
    }
    setVisibleSections(newVisibleSections);
  };

  const toggleAllSections = (): void => {
    if (visibleSections.size === DEBUG_SECTIONS.length) {
      setVisibleSections(new Set());
    } else {
      setVisibleSections(new Set(DEBUG_SECTIONS.map((section) => section.key)));
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div>
      {/* Control Panel */}
      <div className="fixed left-40 top-4 z-50 flex flex-row flex-wrap gap-2">
        <button
          className="rounded-3xl border-2 border-solid border-at bg-none px-4 py-1 text-at transition-colors hover:bg-at hover:text-white"
          onClick={toggleAllSections}
        >
          {visibleSections.size === DEBUG_SECTIONS.length ? 'Hide All' : 'Show All'}
        </button>

        {DEBUG_SECTIONS.map((section) => (
          <button
            key={section.key}
            className={classNames(
              'rounded-3xl border-2 border-solid border-at px-4 py-1 transition-colors',
              visibleSections.has(section.key) ? 'bg-at text-white' : 'bg-none text-at hover:bg-at hover:text-white',
            )}
            onClick={() => toggleSection(section.key)}
          >
            {section.label}
          </button>
        ))}
      </div>

      {/* Debug Panels */}
      {Array.from(visibleSections).map((sectionKey, index) => {
        const section = DEBUG_SECTIONS.find((s) => s.key === sectionKey);
        if (!section) return null;

        const value = storeState[section.key];
        const topOffset = 100 + index * 250; // Stack panels vertically

        return (
          <div
            key={section.key}
            className={classNames('fixed right-10 w-[40ch] rounded-lg bg-white/90 p-4 text-at shadow-lg', textColor)}
            style={{
              top: `${topOffset}px`,
              zIndex: onTop ? 10 : 0,
              maxHeight: '200px',
              overflow: 'auto',
            }}
          >
            <h4 className="mb-2 border-b border-at pb-1 font-bold text-at">{section.label}</h4>
            <pre className="text-xs" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
            </pre>
          </div>
        );
      })}
    </div>
  );
};
